import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Grid
} from '@mui/material';
import { Edit as EditIcon, Assignment as AssignIcon, Calculate as CalcIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { progressHeatColor } from './okrColors';

export default function OkrObjectiveDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scoreHelp, setScoreHelp] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignNotes, setAssignNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, sm] = await Promise.all([api.get(`/okr/${id}`), api.get('/okr/score-model')]);
      setRow(o.data?.data);
      setScoreHelp(sm.data?.data);
    } catch {
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const openAssign = async () => {
    try {
      const r = await api.get('/users', { params: { limit: 500, page: 1, is_active: true } });
      setUsers(r.data?.data || []);
      setSelectedUsers((row.assignments || []).map((a) => a.assignee?.id).filter(Boolean));
      setAssignOpen(true);
    } catch {
      toast.error('Could not load users');
    }
  };

  const submitAssign = async () => {
    try {
      await api.post(`/okr/${id}/assign`, { user_ids: selectedUsers, notes: assignNotes || null });
      toast.success('Assignments updated');
      setAssignOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Assign failed');
    }
  };

  if (loading) return <LinearProgress />;
  if (!row) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Not found</Typography>
      </Box>
    );
  }

  const score = Number(row.computed_score || 0);
  const heat = progressHeatColor(score);
  const cat = row.category;

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {row.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {cat?.name} · {row.period_label || 'Period TBD'} · <Chip size="small" label={row.status} sx={{ ml: 0.5 }} />
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {hasPermission('okr_modify') && (
            <>
              <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/okr/objectives/${id}/edit`)}>
                Edit
              </Button>
              <Button variant="contained" startIcon={<AssignIcon />} onClick={openAssign}>
                Assign to employees
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2, bgcolor: heat.bg, border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="caption" sx={{ color: heat.fg, fontWeight: 600 }}>
                Objective score
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, color: heat.fg }}>
                {score.toFixed(1)}%
              </Typography>
              <Typography variant="caption" sx={{ color: heat.fg }}>
                {heat.label}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Key Results category weight
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {cat ? `${Number(cat.weight_percent)}%` : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Portfolio contribution (score × Key Results category weight):{' '}
                <strong>{Number(row.weighted_contribution || 0).toFixed(2)}</strong>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalcIcon fontSize="inherit" /> Score model
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {scoreHelp?.objective_score}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {row.description ? (
        <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
          {row.description}
        </Typography>
      ) : null}

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Key results
      </Typography>
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Area</TableCell>
              <TableCell align="right">Weight</TableCell>
              <TableCell align="right">Progress</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(row.keyResults || []).map((kr) => {
              const p = Number(kr.progress_percent || 0);
              const h = progressHeatColor(p);
              return (
                <TableRow key={kr.id}>
                  <TableCell>
                    <Chip size="small" label={kr.kr_code} variant="outlined" />
                  </TableCell>
                  <TableCell>{kr.title}</TableCell>
                  <TableCell>{kr.objectiveArea?.label || kr.objective_area?.label || '—'}</TableCell>
                  <TableCell align="right">{Number(kr.weight_percent).toFixed(1)}%</TableCell>
                  <TableCell align="right">
                    <Chip size="small" label={`${p.toFixed(0)}%`} sx={{ bgcolor: h.bg, color: h.fg }} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Assigned employees
      </Typography>
      {(row.assignments || []).length === 0 ? (
        <Typography color="text.secondary">No assignments yet.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {row.assignments.map((a) => (
            <Chip
              key={a.id}
              label={`${a.assignee?.first_name || ''} ${a.assignee?.last_name || ''} (${a.assignee?.email})`.trim()}
              variant="outlined"
            />
          ))}
        </Box>
      )}

      <Alert severity="info" sx={{ mt: 4 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Final score model (summary)
        </Typography>
        <Typography variant="body2">{scoreHelp?.portfolio_contribution}</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {scoreHelp?.heatmap}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {scoreHelp?.category_weights}
        </Typography>
      </Alert>

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign objective</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            SelectProps={{ multiple: true }}
            label="Employees"
            margin="normal"
            value={selectedUsers}
            onChange={(e) => setSelectedUsers(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            helperText="Managers can assign one or many employees."
          >
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.first_name} {u.last_name} — {u.email}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Notes (optional)"
            margin="normal"
            multiline
            minRows={2}
            value={assignNotes}
            onChange={(e) => setAssignNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitAssign}>
            Save assignments
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
